<template>
    <b-container fluid class="px-md-5">
        <h5 class="mt-4 p-2 text-center text-primary text-uppercase">Reporte de Agentes</h5>
        
        <!-- Selector de tipo de búsqueda -->
        <b-row class="justify-content-center mb-3">
            <b-col cols="12" md="10">
                <b-card class="shadow-sm">
                    <b-form-group label="Tipo de búsqueda:" label-class="font-weight-bold">
                        <b-form-radio-group v-model="searchType" :options="searchTypeOptions" 
                            button-variant="outline-primary" buttons @change="onSearchTypeChange">
                        </b-form-radio-group>
                    </b-form-group>
                </b-card>
            </b-col>
        </b-row>

        <!-- Campos de fecha según tipo de búsqueda -->
        <b-row class="justify-content-center">
            <!-- Día específico -->
            <b-col v-if="searchType === 'single'" cols="12" md="6" align-self="center" class="mt-2">
                <b-form-group label="Fecha:" label-class="font-weight-bold">
                    <b-form-datepicker name="singleDate" v-model="form.start" v-bind="labels['es'] || {}"
                        v-validate="'required:true|date_format:yyyy-MM-dd'" :state="validateState('start')"
                        :data-vv-as="'fecha'" :disabled="loading"
                        aria-describedby="single-date-feedback" locale="es" :min="minDate" :max="maxDate" 
                        hide-header no-flip class="text-left"></b-form-datepicker>
                    <b-form-invalid-feedback id="single-date-feedback">{{ veeErrors.first('start')
                        }}</b-form-invalid-feedback>
                </b-form-group>
            </b-col>

            <!-- Rango de fechas -->
            <template v-if="searchType === 'range'">
                <b-col cols="12" md="4" align-self="center" class="mt-2">
                    <b-form-group label="Fecha inicio:" label-class="font-weight-bold">
                        <b-form-datepicker name="start" v-model="form.start" v-bind="labels['es'] || {}"
                            v-validate="'required:true|date_format:yyyy-MM-dd'" :state="validateState('start')"
                            :data-vv-as="'fecha inicio'" :disabled="loading"
                            aria-describedby="start-live-feedback" locale="es" :min="minDate" :max="form.end || maxDate" 
                            hide-header no-flip class="text-left"></b-form-datepicker>
                        <b-form-invalid-feedback id="start-live-feedback">{{ veeErrors.first('start')
                            }}</b-form-invalid-feedback>
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="4" align-self="center" class="mt-2">
                    <b-form-group label="Fecha fin:" label-class="font-weight-bold">
                        <b-form-datepicker name="end" v-model="form.end" v-bind="labels['es'] || {}"
                            v-validate="'required:true|date_format:yyyy-MM-dd'" :state="validateState('end')"
                            :data-vv-as="'fecha fin'" :disabled="loading"
                            aria-describedby="end-live-feedback" locale="es" :min="form.start || minDate" :max="maxDate" 
                            hide-header no-flip class="text-left"></b-form-datepicker>
                        <b-form-invalid-feedback id="end-live-feedback">{{ veeErrors.first('end')
                            }}</b-form-invalid-feedback>
                    </b-form-group>
                </b-col>
            </template>

            <!-- Botones -->
            <b-col cols="12" md="2" align-self="center" class="mt-2">
                <b-button block variant="primary" class="shadow" @click="getIni">
                    <i class="material-icons">search</i>
                    <span>Filtrar</span>
                </b-button>
            </b-col>
            <b-col cols="12" md="2" align-self="center" class="mt-2">
                <b-button block variant="info" class="shadow" @click="showDashboard" :disabled="items.length === 0">
                    <i class="material-icons">bar_chart</i>
                    <span>Estadísticas</span>
                </b-button>
            </b-col>
            <b-col cols="12" md="2" align-self="center" class="mt-2">
                <b-button block variant="success" class="shadow" @click="getDownload">
                    <i class="material-icons">download</i>
                    <span>Descargar</span>
                </b-button>
            </b-col>
        </b-row>

        <!-- Selector de columnas -->
        <b-row class="justify-content-center mt-3">
            <b-col cols="12">
                <b-card class="shadow-sm mb-3" no-body>
                    <b-card-header class="bg-white border-bottom">
                        <div class="d-flex justify-content-between align-items-center">
                            <h6 class="mb-0">
                                <i class="material-icons align-middle mr-1">view_column</i>
                                Columnas Visibles
                            </h6>
                            <div>
                                <b-button variant="outline-primary" size="sm" @click="selectAllColumns" class="mr-2">
                                    <i class="material-icons" style="font-size: 16px;">done_all</i>
                                    Todas
                                </b-button>
                                <b-button variant="outline-secondary" size="sm" @click="deselectAllColumns">
                                    <i class="material-icons" style="font-size: 16px;">clear_all</i>
                                    Ninguna
                                </b-button>
                            </div>
                        </div>
                    </b-card-header>
                    <b-card-body class="p-3">
                        <div class="column-selector-grid">
                            <b-badge 
                                v-for="option in columnOptions" 
                                :key="option.value"
                                :variant="isColumnSelected(option.value) ? 'primary' : 'light'"
                                class="column-badge"
                                @click="toggleColumn(option.value)"
                                pill
                            >
                                <i class="material-icons" style="font-size: 14px;">
                                    {{ isColumnSelected(option.value) ? 'check_circle' : 'radio_button_unchecked' }}
                                </i>
                                {{ option.text }}
                            </b-badge>
                        </div>
                        <small class="text-muted mt-2 d-block">
                            <i class="material-icons" style="font-size: 14px;">info</i>
                            Haz clic en las etiquetas para seleccionar/deseleccionar columnas
                            <span v-if="selectedColumns.length > 0" class="ml-2">
                                ({{ selectedColumns.length }} columna{{ selectedColumns.length !== 1 ? 's' : '' }} seleccionada{{ selectedColumns.length !== 1 ? 's' : '' }})
                            </span>
                        </small>
                    </b-card-body>
                </b-card>
            </b-col>
        </b-row>

        <b-row class="justify-content-center">
            <b-col cols="12" class="mt-4 mb-2">
                <b-form-group class="mb-0">
                    <b-input-group size="sm">
                        <b-form-input id="search" v-model="search" type="search" 
                            placeholder="Buscar (puedes usar múltiples términos separados por espacios o comas)"
                            class="pad-3-15 btlr-30 bblr-30"></b-form-input>
                        <b-input-group-append>
                            <b-button variant="default" :disabled="!search" @click="search = ''"
                                class="m-0 btlr-0 bblr-0">
                                <i class="material-icons mdi mdi-close"></i>
                            </b-button>
                        </b-input-group-append>
                    </b-input-group>
                    <small v-if="search" class="text-muted">
                        <i class="material-icons" style="font-size: 14px;">info</i>
                        Mostrando {{ items.length }} resultado{{ items.length !== 1 ? 's' : '' }} para "{{ search }}"
                    </small>
                </b-form-group>
            </b-col>
            <b-col cols="12" class="px-0">
                <b-alert v-if="selectedColumns.length === 0" show variant="warning" class="text-center">
                    <i class="material-icons align-middle">warning</i>
                    <strong>Selecciona al menos una columna para visualizar los datos</strong>
                </b-alert>
                <b-table v-else :items="items" :fields="visibleFields" responsive="sm" class="my-2 border-bottom" stacked="sm"
                    hover small show-empty no-border-collapse table-variant="light" details-td-class="px-0"
                    empty-filtered-text="No hay transacciones que coincidan con la búsqueda"
                    empty-text="No hay transacciones para mostrar" :current-page="currentPage" :per-page="perPage"
                    :busy="isBusy" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" @sort-changed="handleSortChange">
                    <template #table-busy>
                        <div class="text-center text-danger my-4">
                            <b-spinner class="align-middle"></b-spinner>
                            <strong>Actualizando...</strong>
                        </div>
                    </template>

                    <template #cell(int_id)="row">
                        {{ row.item.payer === 'REMITEE' && row.item.int_id !== null ? '**' + (row.item.int_id).slice(-8)
                            : row.item.int_id }}
                    </template>

                    <template #cell(total_text)="row">
                        <template v-if="row.item.type === '+'">
                            <b class="text-success">{{ row.item.total_text }}</b>
                        </template>
                        <template v-else>
                            <b class="text-danger">{{ row.item.total_text }}</b>
                        </template>
                    </template>

                    <template #cell(balance_text)="row">
                        <b class="text-black">{{ row.item.balance_text }}</b>
                    </template>

                    <template #bottom-row>
                        <td v-for="(field, index) in visibleFields" :key="field.key"
                            :class="{ 'text-right': index > 0 }">
                            <strong v-if="index === 0">Totales</strong>
                            <template v-else-if="sumFields.some(sf => sf.key === field.key)">
                                {{ getPageTotal(field.key) }}
                            </template>
                        </td>
                    </template>
                </b-table>
            </b-col>
            <b-col sm="5" md="6">
                <b-form-group label="Mostrar:" label-for="per-page-select" label-cols-sm="4" label-cols-md="6"
                    label-cols-lg="9" label-align-sm="right" label-size="sm" class="mt-2 mb-8">
                    <b-form-select id="per-page-select" v-model="perPage" :options="pageOptions"
                        size="sm"></b-form-select>
                </b-form-group>
            </b-col>
            <b-col sm="7" md="6">
                <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" align="fill" size="sm"
                    class="mt-2 mb-8"></b-pagination>
            </b-col>
        </b-row>

        <!-- Modal de Dashboard Estadístico -->
        <b-modal id="dashboard-modal" size="xl" title="Dashboard Estadístico" hide-footer>
            <b-container fluid>
                <!-- Tarjetas de resumen -->
                <b-row class="mb-4">
                    <b-col cols="6" md="4" lg="2" class="mb-3" v-for="stat in dashboardStats" :key="stat.label">
                        <b-card class="text-center shadow-sm" :style="'border-left: 4px solid ' + stat.color">
                            <h6 class="text-muted mb-2">{{ stat.label }}</h6>
                            <h4 class="mb-0" :style="'color: ' + stat.color">{{ stat.value }}</h4>
                        </b-card>
                    </b-col>
                </b-row>

                <!-- Gráfico de barras -->
                <b-row>
                    <b-col cols="12">
                        <b-card class="shadow-sm">
                            <h5 class="text-center mb-4">Resumen Financiero</h5>
                            <canvas ref="chartCanvas" height="100"></canvas>
                        </b-card>
                    </b-col>
                </b-row>

                <b-row class="mt-3">
                    <b-col cols="12" class="text-center">
                        <b-button variant="secondary" @click="$bvModal.hide('dashboard-modal')">
                            Cerrar
                        </b-button>
                    </b-col>
                </b-row>
            </b-container>
        </b-modal>
    </b-container>
</template>

<script>
import axios from "axios";
import Chart from 'chart.js';

export default {
    name: "ReportAgente",
    props: ['user'],
    data: () => ({
        isBusy: true,
        search: '',
        searchDebounce: null,
        sortBy: 'date',
        sortDesc: true,
        searchType: 'single',
        searchTypeOptions: [
            { text: 'Día específico', value: 'single' },
            { text: 'Rango de fechas', value: 'range' }
        ],
        chartInstance: null,
        dashboardStats: [],
        allFields: [
            { key: 'date', label: 'Fecha', sortable: true },
            { key: 'payment_id', label: 'ID', sortable: true, thClass: 'text-sm-right', tdClass: 'text-sm-right' },
            { key: 'int_id', label: 'Código', sortable: true, thClass: 'text-sm-right', tdClass: 'text-sm-right' },
            { key: 'payer', label: 'Corresponsal', sortable: true },
            { key: 'partner_name', label: 'Agente', sortable: true },
            { key: 'branch_name', label: 'Sucursal', sortable: true },
            { key: 'country_receiver', label: 'Destino', sortable: true },
            { key: 'status', label: 'Estado', sortable: true },
            { key: 'base_text', label: 'Monto enviado', sortable: true, thClass: 'text-sm-right', tdClass: 'text-sm-right' },
            { key: 'fee_text', label: 'Tarifa Cliente', sortable: true, thClass: 'text-sm-right', tdClass: 'text-sm-right' },
            { key: 'total_text', label: 'Monto total', sortable: true, thClass: 'text-sm-right', tdClass: 'text-sm-right' },
            { key: 'fee_net', label: 'Tarifa Cliente Neta', sortable: true, thClass: 'text-sm-right', tdClass: 'text-sm-right' },
            { key: 'fee_tax', label: 'IVA', sortable: true, thClass: 'text-sm-right', tdClass: 'text-sm-right' },
            { key: 'refund', label: 'Devolucion', sortable: true, thClass: 'text-sm-right', tdClass: 'text-sm-right' },
            { key: 'prefunding', label: 'Prefondeo', sortable: true, thClass: 'text-sm-right', tdClass: 'text-sm-right' },
            { key: 'balance_text', label: 'Saldo', sortable: true, thClass: 'text-sm-right', tdClass: 'text-sm-right' },
            { key: 'gain_text', label: 'Comision Agente', sortable: true, thClass: 'text-sm-right', tdClass: 'text-sm-right' }
        ],
        selectedColumns: [
            'date', 'payment_id', 'int_id', 'payer', 'partner_name', 'branch_name', 'country_receiver', 
            'status', 'base_text', 'fee_text', 'total_text', 'balance_text', 'gain_text'
        ],
        totalRows: 1,
        currentPage: 1,
        perPage: 25,
        pageOptions: [25, 50, 100, { value: -1, text: "Todos" }],
        filteredTableItems: [],
        items: [],
        loading: false,
        labels: {
            start: 'Fecha Inicial',
            end: 'Fecha Final',
            es: {
                labelPrevDecade: 'Década Anterior',
                labelPrevYear: 'Año Anterior',
                labelPrevMonth: 'Mes Anterior',
                labelCurrentMonth: 'Mes Actual',
                labelNextMonth: 'Próximo Mes',
                labelNextYear: 'Próximo Año',
                labelNextDecade: 'Próxima Década',
                labelToday: 'Hoy',
                labelSelected: 'Fecha Seleccionada',
                labelNoDateSelected: 'Sin fecha seleccionada',
                labelCalendar: 'Calendario',
                labelNav: 'Navegar por el calendario',
                labelHelp: 'Use las teclas de flecha para navegar por el calendario'
            }
        },
        form: {
            start: undefined,
            end: undefined
        },
        minDate: undefined,
        maxDate: undefined,
    }),
    async mounted() {
        this.$emit('loader');
        this.$emit('section', 'reports');
        this.getDate();
        await this.getIni();
    },
    computed: {
        columnOptions() {
            return this.allFields.map(field => ({
                text: field.label,
                value: field.key
            }));
        },
        visibleFields() {
            return this.allFields.filter(field => this.selectedColumns.includes(field.key));
        },
        sumFields() {
            return this.visibleFields.filter(f => 
                ['base_text', 'com_text', 'total_text', 'fee_net', 'fee_tax', 'fee_text', 
                 'amount_text', 'gain_text', 'refund', 'prefunding'].includes(f.key)
            );
        },
        pagedItems() {
            let itemsToPaginate = this.items;
            if (Array.isArray(this.filteredTableItems) && this.filteredTableItems.length > 0) {
                itemsToPaginate = this.filteredTableItems;
            }
            return itemsToPaginate.slice((this.currentPage - 1) * this.perPage, this.currentPage * this.perPage);
        }
    },
    methods: {
        async getIni() {
            this.$validator.validateAll().then(result => {
                if (!result) {
                    return;
                }
                
                // Si es búsqueda por día específico, end debe ser igual a start
                let endDate = this.searchType === 'single' ? this.form.start : this.form.end;
                
                this.isBusy = true;
                axios.put('/api/admin/reports/agente', {
                    start: this.form.start,
                    end: endDate,
                    search: this.search,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc
                }).then((res) => {
                    if (res.data.status === 'success') {
                        this.items = res.data.items;
                        this.filteredTableItems = res.data.items;
                    }
                }).catch(err => {
                    this.$root.$bvToast.toast(err.response.data.errors || 'Error al cargar datos', {
                        title: 'Notificación',
                        headerClass: 'd-none',
                        variant: 'danger',
                        toaster: 'b-toaster-top-right',
                        autoHideDelay: 5000,
                        solid: true,
                        appendToast: true
                    });
                }).finally(() => {
                    this.isBusy = false;
                    this.totalRows = this.items.length;
                });
            });
        },
        getDate() {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const minDate = new Date(today);
            minDate.setFullYear(minDate.getFullYear() - 100);
            const start = new Date(today);
            start.setMonth(start.getMonth() - 1);

            this.minDate = minDate;
            this.maxDate = today;
            this.form.start = start.toISOString().slice(0, 10);
            this.form.end = today.toISOString().slice(0, 10);
        },
        getDownload() {
            if (this.selectedColumns.length === 0) {
                this.$root.$bvToast.toast('Debes seleccionar al menos una columna para exportar', {
                    title: 'Notificación',
                    headerClass: 'd-none',
                    variant: 'warning',
                    toaster: 'b-toaster-top-right',
                    autoHideDelay: 5000,
                    solid: true,
                    appendToast: true
                });
                return;
            }
            
            // Los items ya están filtrados del servidor
            const dataToExport = this.items;
            
            if (dataToExport.length === 0) {
                this.$root.$bvToast.toast('No hay datos para exportar con los filtros aplicados', {
                    title: 'Notificación',
                    headerClass: 'd-none',
                    variant: 'warning',
                    toaster: 'b-toaster-top-right',
                    autoHideDelay: 5000,
                    solid: true,
                    appendToast: true
                });
                return;
            }
            
            // Calcular totales para las columnas numéricas
            const totals = this.calculateTotals(dataToExport);
            const columns = this.selectedColumns.join(',');
            
            // Determinar fecha fin según tipo de búsqueda
            const endDate = this.searchType === 'single' ? this.form.start : this.form.end;
            
            // Enviar datos filtrados al backend con totales
            axios.post('/api/export/reports/agente/filtered', {
                columns: columns,
                data: dataToExport,
                totals: totals,
                transactionCount: dataToExport.length,
                start: this.form.start,
                end: endDate
            }, {
                responseType: 'blob'
            }).then((res) => {
                const blob = new Blob([res.data], { type: res.data.type });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                const name = 'Reporte-Agente-(' + this.form.start + ')-(' + endDate + ').xlsx';
                link.setAttribute('download', name);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }).catch(err => {
                this.$root.$bvToast.toast('Error al descargar el reporte', {
                    title: 'Notificación',
                    headerClass: 'd-none',
                    variant: 'danger',
                    toaster: 'b-toaster-top-right',
                    autoHideDelay: 5000,
                    solid: true,
                    appendToast: true
                });
            });
        },
        validateState(ref) {
            if (this.veeFields[ref] && (this.veeFields[ref].dirty || this.veeFields[ref].validated)) {
                return !this.veeErrors.has(ref);
            }
            return null;
        },
        getPageTotal(key) {
            const total = this.pagedItems.reduce((total, item) => {
                let cleanedItem = item[key];
                if (typeof cleanedItem === 'string') {
                    cleanedItem = cleanedItem.replace(/,/g, '');
                }
                const value = Number(cleanedItem);
                return isNaN(value) ? total : parseFloat(total) + parseFloat(value);
            }, 0);
            return total.toLocaleString("en-US", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            });
        },
        selectAllColumns() {
            this.selectedColumns = this.allFields.map(field => field.key);
        },
        deselectAllColumns() {
            this.selectedColumns = [];
        },
        isColumnSelected(columnKey) {
            return this.selectedColumns.includes(columnKey);
        },
        toggleColumn(columnKey) {
            const index = this.selectedColumns.indexOf(columnKey);
            if (index > -1) {
                this.selectedColumns.splice(index, 1);
            } else {
                this.selectedColumns.push(columnKey);
            }
        },
        handleSortChange(ctx) {
            // ctx contiene: sortBy, sortDesc
            this.sortBy = ctx.sortBy;
            this.sortDesc = ctx.sortDesc;
            this.getIni();
        },
        calculateTotals(data) {
            const numericFields = ['base', 'fee', 'total', 'fee_net', 'fee_tax', 'refund', 'prefunding', 'balance', 'gain'];
            const totals = {};
            
            numericFields.forEach(field => {
                const fieldWithText = field + '_text';
                let sum = 0;
                
                data.forEach(item => {
                    let value = item[fieldWithText] || item[field] || 0;
                    
                    // Limpiar el valor si es string
                    if (typeof value === 'string') {
                        value = value.replace(/,/g, '');
                    }
                    
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue)) {
                        sum += numValue;
                    }
                });
                
                // Guardar tanto el valor numérico como el formateado
                totals[field] = sum;
                totals[fieldWithText] = sum.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            });
            
            return totals;
        },
        onSearchTypeChange() {
            // Cuando cambia el tipo de búsqueda, limpiar fecha fin si es necesario
            if (this.searchType === 'single') {
                this.form.end = this.form.start;
            } else {
                // Si cambia a rango, inicializar end con la fecha de hoy
                const today = new Date();
                this.form.end = today.toISOString().slice(0, 10);
            }
        },
        showDashboard() {
            // Calcular estadísticas
            const stats = this.calculateDashboardStats();
            
            // Preparar datos para las tarjetas
            this.dashboardStats = [
                {
                    label: 'Cantidad',
                    value: stats.cantidad.toLocaleString(),
                    color: '#6c757d'
                },
                {
                    label: 'Monto Total',
                    value: '$' + stats.monto.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                    color: '#17a2b8'
                },
                {
                    label: 'Comisión Cobrada',
                    value: '$' + stats.comisionCobrada.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                    color: '#28a745'
                },
                {
                    label: 'Comisión Ganada',
                    value: '$' + stats.comisionGanada.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                    color: '#007bff'
                },
                {
                    label: 'Comisión Enviada',
                    value: '$' + stats.comisionEnviada.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                    color: '#ffc107'
                }
            ];
            
            // Mostrar el modal
            this.$bvModal.show('dashboard-modal');
            
            // Esperar a que el modal se renderice completamente
            this.$nextTick(() => {
                this.renderChart(stats);
            });
        },
        calculateDashboardStats() {
            const data = this.items;
            
            let monto = 0;
            let comisionCobrada = 0;
            let comisionGanada = 0;
            let cantidad = data.length;
            
            data.forEach(item => {
                // Monto enviado
                let baseValue = item.base || 0;
                if (typeof baseValue === 'string') {
                    baseValue = parseFloat(baseValue.replace(/,/g, ''));
                }
                monto += isNaN(baseValue) ? 0 : baseValue;
                
                // Comisión cobrada (tarifa cliente)
                let feeValue = item.fee || 0;
                if (typeof feeValue === 'string') {
                    feeValue = parseFloat(feeValue.replace(/,/g, ''));
                }
                comisionCobrada += isNaN(feeValue) ? 0 : feeValue;
                
                // Comisión ganada (ganancia agente)
                let gainValue = item.gain || 0;
                if (typeof gainValue === 'string') {
                    gainValue = parseFloat(gainValue.replace(/,/g, ''));
                }
                comisionGanada += isNaN(gainValue) ? 0 : gainValue;
            });
            
            // Comisión enviada = tarifa cliente - comisión ganada
            const comisionEnviada = comisionCobrada - comisionGanada;
            
            return {
                cantidad,
                monto,
                comisionCobrada,
                comisionGanada,
                comisionEnviada
            };
        },
        renderChart(stats) {
            // Destruir gráfico anterior si existe
            if (this.chartInstance) {
                this.chartInstance.destroy();
            }
            
            const ctx = this.$refs.chartCanvas.getContext('2d');
            
            this.chartInstance = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels: ['Monto Total', 'Comisión Cobrada', 'Comisión Ganada', 'Comisión Enviada'],
                    datasets: [{
                        label: 'Valores en $',
                        data: [
                            stats.monto,
                            stats.comisionCobrada,
                            stats.comisionGanada,
                            stats.comisionEnviada
                        ],
                        backgroundColor: [
                            'rgba(23, 162, 184, 0.7)',
                            'rgba(40, 167, 69, 0.7)',
                            'rgba(0, 123, 255, 0.7)',
                            'rgba(255, 193, 7, 0.7)'
                        ],
                        borderColor: [
                            'rgba(23, 162, 184, 1)',
                            'rgba(40, 167, 69, 1)',
                            'rgba(0, 123, 255, 1)',
                            'rgba(255, 193, 7, 1)'
                        ],
                        borderWidth: 2
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    legend: {
                        display: false
                    },
                    tooltips: {
                        callbacks: {
                            label: function(tooltipItem, data) {
                                let label = data.datasets[tooltipItem.datasetIndex].label || '';
                                if (label) {
                                    label += ': ';
                                }
                                label += '$' + tooltipItem.yLabel.toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                });
                                return label;
                            }
                        }
                    },
                    scales: {
                        yAxes: [{
                            ticks: {
                                beginAtZero: true,
                                callback: function(value) {
                                    return '$' + value.toLocaleString('en-US');
                                }
                            }
                        }]
                    }
                }
            });
        }
    },
    beforeDestroy() {
        // Limpiar el gráfico al destruir el componente
        if (this.chartInstance) {
            this.chartInstance.destroy();
        }
    },
    watch: {
        search(newVal) {
            // Debouncing: espera 500ms después de que el usuario termine de escribir
            clearTimeout(this.searchDebounce);
            this.searchDebounce = setTimeout(() => {
                this.getIni();
            }, 500);
        }
    }
}
</script>

<style scoped>
.column-selector-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 10px;
}

.column-badge {
    cursor: pointer;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 500;
    transition: all 0.2s ease;
    user-select: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.column-badge:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.column-badge .material-icons {
    vertical-align: middle;
}

.badge-primary {
    background-color: #007bff !important;
    color: white !important;
}

.badge-light {
    background-color: #f8f9fa !important;
    color: #6c757d !important;
    border: 1px solid #dee2e6;
}

.badge-light:hover {
    background-color: #e9ecef !important;
    border-color: #adb5bd;
}
</style>

